<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCrmStore } from 'dashboard/stores/crm';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const emit = defineEmits(['edit']);
const { t } = useI18n();
const crmStore = useCrmStore();

const opportunity = computed(() => crmStore.selectedOpportunity);
const isVisible = computed(() => opportunity.value !== null);

const close = () => crmStore.selectOpportunity(null);

const showCloseForm = ref(false);
const lostReason = ref('');

const closeOpportunity = async () => {
  const updated = await crmStore.updateOpportunity(opportunity.value.id, {
    status: 'lost',
    lost_reason: lostReason.value,
  });
  if (updated) {
    showCloseForm.value = false;
    lostReason.value = '';
  }
};

const statusClass = {
  won: 'bg-woot-25 text-woot-600',
  lost: 'bg-n-ruby-3 text-n-ruby-11',
  open: 'bg-n-iris-3 text-n-iris-11',
};

const statusLabel = computed(() => {
  const map = {
    won: t('CRM.PANEL.STATUS_WON'),
    lost: t('CRM.PANEL.STATUS_LOST'),
    open: t('CRM.PANEL.STATUS_OPEN'),
  };
  return map[opportunity.value?.status] || opportunity.value?.status;
});
</script>

<template>
  <div
    v-if="isVisible"
    class="w-[400px] flex-shrink-0 h-full border-l border-n-weak bg-n-surface-1 flex flex-col overflow-hidden"
  >
    <div
      class="flex items-center justify-between px-4 py-3 border-b border-n-weak"
    >
      <h3 class="text-sm font-semibold text-n-slate-12 truncate flex-1">
        {{ opportunity.title }}
      </h3>
      <button
        class="text-n-slate-10 hover:text-n-slate-12 ml-2 text-lg leading-none"
        @click="close"
      >
        {{ '×' }}
      </button>
    </div>
    <div class="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
      <span
        class="text-xs font-medium px-2 py-0.5 rounded-full self-start"
        :class="[statusClass[opportunity.status] || statusClass.open]"
      >
        {{ statusLabel }}
      </span>
      <div v-if="opportunity.value_cents !== null">
        <p class="text-xs text-n-slate-10">{{ t('CRM.PANEL.VALUE') }}</p>
        <p class="text-sm font-semibold text-n-slate-12">
          {{ opportunity.value_display }}
        </p>
      </div>
      <div>
        <p class="text-xs text-n-slate-10">{{ t('CRM.PANEL.STAGE') }}</p>
        <div class="flex items-center gap-1.5">
          <span
            class="inline-block w-2 h-2 rounded-full"
            :style="{ backgroundColor: opportunity.stage.color }"
          />
          <span class="text-sm text-n-slate-12">{{
            opportunity.stage.name
          }}</span>
        </div>
      </div>
      <div>
        <p class="text-xs text-n-slate-10">{{ t('CRM.PANEL.FUNNEL') }}</p>
        <p class="text-sm text-n-slate-12">{{ opportunity.funnel.name }}</p>
      </div>
      <div v-if="opportunity.status === 'lost' && opportunity.lost_reason">
        <p class="text-xs text-n-slate-10">{{ t('CRM.PANEL.LOST_REASON') }}</p>
        <p class="text-sm text-n-slate-12">{{ opportunity.lost_reason }}</p>
      </div>
      <div v-if="opportunity.contact">
        <p class="text-xs text-n-slate-10">{{ t('CRM.PANEL.CONTACT') }}</p>
        <div class="flex items-center gap-2">
          <Avatar
            :src="opportunity.contact.avatar_url"
            :name="opportunity.contact.name"
            :size="24"
            rounded-full
          />
          <span class="text-sm text-n-slate-12">{{
            opportunity.contact.name
          }}</span>
        </div>
      </div>
      <div v-if="opportunity.assigned_agent">
        <p class="text-xs text-n-slate-10">{{ t('CRM.PANEL.AGENT') }}</p>
        <div class="flex items-center gap-2">
          <Avatar
            :src="opportunity.assigned_agent.avatar_url"
            :name="opportunity.assigned_agent.name"
            :size="24"
            rounded-full
          />
          <span class="text-sm text-n-slate-12">{{
            opportunity.assigned_agent.name
          }}</span>
        </div>
      </div>
    </div>
    <div class="px-4 py-3 border-t border-n-weak flex flex-col gap-2">
      <div v-if="showCloseForm" class="flex flex-col gap-2">
        <input
          v-model="lostReason"
          type="text"
          class="w-full text-sm border border-n-weak rounded px-3 py-1.5 bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-n-ruby-6"
          :placeholder="t('CRM.FORM.PLACEHOLDER_LOST_REASON')"
        />
        <div class="flex gap-2">
          <button
            class="flex-1 text-sm border border-n-ruby-6 rounded px-3 py-1.5 text-n-ruby-11 hover:bg-n-ruby-3 transition-colors disabled:opacity-50"
            :disabled="!lostReason.trim()"
            @click="closeOpportunity"
          >
            {{ t('CRM.PANEL.CLOSE_OPPORTUNITY') }}
          </button>
          <button
            class="flex-1 text-sm border border-n-weak rounded px-3 py-1.5 text-n-slate-12 hover:bg-n-slate-2 transition-colors"
            @click="showCloseForm = false"
          >
            {{ t('CRM.FORM.CANCEL') }}
          </button>
        </div>
      </div>
      <div v-else class="flex gap-2">
        <button
          v-if="opportunity.status === 'open'"
          class="flex-1 text-sm border border-n-ruby-6 rounded px-3 py-1.5 text-n-ruby-11 hover:bg-n-ruby-3 transition-colors"
          @click="showCloseForm = true"
        >
          {{ t('CRM.PANEL.CLOSE_OPPORTUNITY') }}
        </button>
        <button
          class="flex-1 text-sm border border-n-weak rounded px-3 py-1.5 text-n-slate-12 hover:bg-n-slate-2 transition-colors"
          @click="emit('edit', opportunity.id)"
        >
          {{ t('CRM.PANEL.EDIT') }}
        </button>
      </div>
    </div>
  </div>
</template>
